/* eslint-disable jsx-a11y/anchor-is-valid */
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import {
  menuBookEditorActions,
  selectMenuBookEditorExpandAll,
  selectMenuBookEditorFormSearchValue,
} from 'features/MenuBook/menuBookEditorSlice';
import { ChildSubMenuGroup } from 'features/MenuBook/models';
import DisplaySubMenuGroupDetail from 'features/SubMenuList/pages/SubMenuGroupDetail';
import { Suspense, memo, useEffect, useRef, useState } from 'react';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { ModalProps } from 'react-bootstrap';
import { MINUS_TEXT, PLUS_TEXT, PRIMARY_BUTTON } from 'utils';
import { wasMultiSelectKeyUsed, wasToggleInSelectionGroupKeyUsed } from '../../utils';
import { COLUMN_WIDTH, getItemStyle } from '../MenuBookEditorModal';

export interface ChildSubMenuGroupRowProps {
  genreId: number;
  subgenreId: number;
  menuId: number;
  subMenuGroupId: number;
  subMenuId: number;
  childSubMenuGroupId: number;
  children?: React.ReactNode;
  index: number;
}

function ChildSubMenuGroupRow({
  genreId,
  subgenreId,
  menuId,
  subMenuGroupId,
  subMenuId,
  childSubMenuGroupId,
  children,
  index,
}: ChildSubMenuGroupRowProps) {
  const dispatch = useAppDispatch();

  const childSubMenuGroup = useAppSelector((state) => {
    return state.menuBookEditor.data
      .flatMap((genre) => (genre.id === genreId ? genre.subgenres : []))
      .flatMap((subgenre) => (subgenre.id === subgenreId ? subgenre.menus : []))
      .flatMap((menu) => (menu.id === menuId ? menu.sub_menu_groups : []))
      .flatMap((subMenuGroup) => (subMenuGroup.id === subMenuGroupId ? subMenuGroup.sub_menus : []))
      .flatMap((subMenu) => (subMenu.id === subMenuId ? subMenu.child_sub_menu_groups : []))
      .find((childSubMenuGroup) => childSubMenuGroup.id === childSubMenuGroupId);
  }) as ChildSubMenuGroup;

  const isExpand = useAppSelector(selectMenuBookEditorExpandAll);
  const formSearchValue = useAppSelector(selectMenuBookEditorFormSearchValue);

  const [open, setOpen] = useState(isExpand);
  const [showChildSubMenuGroupModal, setShowChildSubMenuGroupModal] = useState(false);

  // ref
  const subMenuGroupDetailRef = useRef<ModalProps>(DisplaySubMenuGroupDetail);
  const isLoadedChildRef = useRef<boolean>(false);

  const pathId = `${genreId}-${subgenreId}-${menuId}-${subMenuGroupId}-${subMenuId}-${childSubMenuGroup.id}`;
  const droppableId = `menu-${genreId}-${subgenreId}-${menuId}-${subMenuGroupId}-${subMenuId}`;
  const isSelected = false;
  const isGhosting = false;
  const mbEditorContentElement = document.getElementById('mb-editor-table');

  useEffect(() => {
    const btnExpandElem = document.querySelector(
      `#child_sub_menu_group-${pathId}-row .dan-btn-expand`
    ) as HTMLButtonElement;
    function handleBtnExpandElemClick(event: MouseEvent) {
      event.stopPropagation();
      setOpen(!open);
      isLoadedChildRef.current = true;
      btnExpandElem.innerHTML = !open ? MINUS_TEXT : PLUS_TEXT;
      btnExpandElem?.setAttribute('aria-expanded', !open ? 'true' : 'false');
    }

    if (btnExpandElem) {
      btnExpandElem.addEventListener('click', handleBtnExpandElemClick);
    }

    return () => {
      if (btnExpandElem) {
        btnExpandElem.removeEventListener('click', handleBtnExpandElemClick);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (isExpand !== undefined) {
      setOpen(isExpand && isLoadedChildRef.current);
    }
  }, [isExpand]);

  useEffect(() => {
    if (showChildSubMenuGroupModal) {
      handleOpenPopupSelectedSubMenuGroupId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showChildSubMenuGroupModal]);

  function onKeyDown(
    event: React.KeyboardEvent<HTMLDivElement>,
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot
  ) {
    if (event.defaultPrevented) {
      return;
    }

    if (snapshot.isDragging) {
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    // we are using the event for selection
    event.preventDefault();

    performAction(event);
  }

  // Using onClick as it will be correctly
  // preventing if there was a drag
  function onClick(event: React.MouseEvent<HTMLDivElement>) {
    const targetElement = event.target as HTMLElement;
    const isInsideParent = mbEditorContentElement && mbEditorContentElement.contains(targetElement);
    if (!isInsideParent) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    if (event.button !== PRIMARY_BUTTON) {
      return;
    }

    // marking the event as used
    // const targetElement = event.target as HTMLElement;
    if (
      !targetElement.classList.contains('form-check-input') &&
      !targetElement.classList.contains('dan-btn-expand') &&
      !Array.from(targetElement.classList).some((className) => className.includes('btn'))
    ) {
      event.preventDefault();
    } else {
      return;
    }
  }

  function onTouchEnd(event: React.TouchEvent<HTMLDivElement>) {
    const targetElement = event.target as HTMLElement;
    const isInsideParent = mbEditorContentElement && mbEditorContentElement.contains(targetElement);
    if (!isInsideParent) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    // marking the event as used
    // we would also need to add some extra logic to prevent the click
    // if this element was an anchor
    if (
      !targetElement.classList.contains('form-check-input') &&
      !targetElement.classList.contains('dan-btn-expand') &&
      !Array.from(targetElement.classList).some((className) => className.includes('btn'))
    ) {
      event.preventDefault();
    }

    dispatch(
      menuBookEditorActions.toggleSelectionInGroup({ selectedDroppableId: droppableId, itemId: childSubMenuGroup.id })
    );
  }

  function performAction(event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) {
    if (wasToggleInSelectionGroupKeyUsed(event)) {
      dispatch(
        menuBookEditorActions.toggleSelectionInGroup({ selectedDroppableId: droppableId, itemId: childSubMenuGroup.id })
      );
      return;
    }

    if (wasMultiSelectKeyUsed(event)) {
      dispatch(
        menuBookEditorActions.multiSelectTo({
          arg: {
            // data,
            // selectedItemIds,
            type: 'child_sub_menu_group',
            droppableId: droppableId,
          },
          newItemId: childSubMenuGroup.id,
        })
      );
      return;
    }

    dispatch(menuBookEditorActions.toggleSelection({ selectedDroppableId: droppableId, itemId: childSubMenuGroup.id }));
  }

  function handleOpenSubMenuGroupDetailModal() {
    setShowChildSubMenuGroupModal(true);
  }

  function handleOpenPopupSelectedSubMenuGroupId() {
    const targetDate = formSearchValue?.dataDate || new Date();
    subMenuGroupDetailRef.current.openModalExternal(childSubMenuGroup.id, targetDate);
  }

  function handleSubMenuGroupDetailModalClose() {
    //todo reload child_sub_menu_group data
    if (formSearchValue) {
      const targetDate = formSearchValue?.dataDate || new Date();
      dispatch(
        menuBookEditorActions.fetchSubMenuGroupData({ subMenuGroupId: childSubMenuGroup.id, dataDate: targetDate })
      );
    }

    subMenuGroupDetailRef.current.onReset();
    setShowChildSubMenuGroupModal(false);

    // focus on element with id="mb-editor-table-safe-focus"
    requestAnimationFrame(() => {
      const mbEditorContentElement = document.getElementById('mb-editor-table-safe-focus');
      mbEditorContentElement?.focus();
    });
  }

  return (
    <Draggable isDragDisabled={false} draggableId={`child_sub_menu_group-${pathId}`} index={index}>
      {(provided, snapshot) => {
        return (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style, isSelected, isGhosting)}
            onClick={onClick}
            onTouchEnd={onTouchEnd}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => onKeyDown(event, provided, snapshot)}
          >
            <>
              <div id={`child_sub_menu_group-${pathId}-row`} className="dan-tr">
                <div
                  className="dan-td"
                  style={{ width: `${COLUMN_WIDTH.slice(0, 7).reduce((sum, width) => sum + width, 0)}%` }}
                ></div>
                <div
                  className="dan-td"
                  style={{ width: `${COLUMN_WIDTH.slice(7, 10).reduce((sum, width) => sum + width, 0)}%` }}
                >
                  <button
                    className={clsx('dan-btn-expand', {})}
                    tabIndex={-1}
                    aria-controls={`child_sub_menu_group-${pathId}-content`}
                    aria-expanded={false}
                  >
                    {open && isLoadedChildRef.current ? MINUS_TEXT : PLUS_TEXT}
                  </button>
                  <a
                    className="px-0 btn btn-link"
                    style={{ minWidth: 'inherit' }}
                    rel="noreferrer"
                    tabIndex={-1}
                    onClick={handleOpenSubMenuGroupDetailModal}
                  >
                    {childSubMenuGroup.name}
                  </a>
                </div>
              </div>
              <div style={{ display: open && isLoadedChildRef.current ? 'block' : 'none' }}>
                {isLoadedChildRef.current ? (
                  <div id={`child_sub_menu_group-${pathId}-content`}>{children}</div>
                ) : (
                  open && (
                    <Suspense
                      fallback={
                        <div className="dan-tr">
                          <div className="dan-td" style={{ width: '100%' }}>
                            Loading...
                          </div>
                        </div>
                      }
                    >
                      <div id={`child_sub_menu_group-${pathId}-content`}>{children}</div>
                    </Suspense>
                  )
                )}
              </div>

              {showChildSubMenuGroupModal && (
                <DisplaySubMenuGroupDetail
                  ref={subMenuGroupDetailRef}
                  onSubMenuGroupModalClose={handleSubMenuGroupDetailModalClose}
                />
              )}
            </>
          </div>
        );
      }}
    </Draggable>
  );
}

export default memo(ChildSubMenuGroupRow);
